import { configureStore, combineReducers } from '@reduxjs/toolkit';
import {
  shallowEqual,
  TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from 'react-redux';
import { setupListeners } from '@reduxjs/toolkit/query';
import appSlice from './slices/app.slice';
import redirectSlice from './slices/redirect.slice';
import userAmplifySlice from './slices/userAmplify.slices';
import { authApi } from './apiSlice/auth.slice';
import { workshopsApi } from './apiSlice/workshops.slice';
import { workshopTasksApi } from './apiSlice/workshopTasks.slice';
import { authNoHeaderApi } from './apiSlice/authNoHeader.slice';
import { organizationsApi } from './apiSlice/organizations.slice';
import { registrantsApi } from './apiSlice/registrants.slice';
import { kitManagementApi } from './apiSlice/kitManagement.slice';
import { subscibersApi } from './apiSlice/subscibers.slice';
import { workshopAppApi } from './apiSlice/workshopApp.slice';
import { lightboxApi } from './apiSlice/lightbox.slice';
import { familyApi } from './apiSlice/family.slice';
import { tagsApi } from './apiSlice/tags.slice';
import { userGrowApi } from './apiSlice/userGrow.slice';
import { peopleApi } from './apiSlice/people.slice';
import { elcsApi } from './apiSlice/elcs.slice';
import { pillarsApi } from './apiSlice/pillars.slice';
import { pillarsActionPlanApi } from './apiSlice/pillarsActionPlan.slice';
import { storageApi } from './apiSlice/storage.slice';
import { registrantsInviteApi } from './apiSlice/registrantsInvite.slice';
import { reportingApi } from './apiSlice/reporting.slice';
import { organizationKitInventoryApi } from './apiSlice/organizationKitInventory.slice';
import { eventsApi } from './apiSlice/events.slice';
import { ioApi } from './apiSlice/io.slice';
import { notesApi } from './apiSlice/notes.slice';
import { inKindApi } from './apiSlice/inKind.slice';
import { eventParentsApi } from './apiSlice/eventParents.slice';
import { duplicateManagementApi } from './apiSlice/duplicateManagement.slice';

const appReducer = combineReducers({
  appSlice: appSlice.reducer,
  redirectSlice: redirectSlice.reducer,
  userSlice: userAmplifySlice.reducer,
  [userGrowApi.reducerPath]: userGrowApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [authNoHeaderApi.reducerPath]: authApi.reducer,
  [workshopsApi.reducerPath]: workshopsApi.reducer,
  [workshopTasksApi.reducerPath]: workshopTasksApi.reducer,
  [organizationsApi.reducerPath]: organizationsApi.reducer,
  [registrantsApi.reducerPath]: registrantsApi.reducer,
  [kitManagementApi.reducerPath]: kitManagementApi.reducer,
  [subscibersApi.reducerPath]: subscibersApi.reducer,
  [workshopAppApi.reducerPath]: workshopAppApi.reducer,
  [lightboxApi.reducerPath]: lightboxApi.reducer,
  [familyApi.reducerPath]: familyApi.reducer,
  [tagsApi.reducerPath]: tagsApi.reducer,
  [peopleApi.reducerPath]: peopleApi.reducer,
  [elcsApi.reducerPath]: elcsApi.reducer,
  [storageApi.reducerPath]: storageApi.reducer,
  [pillarsApi.reducerPath]: pillarsApi.reducer,
  [pillarsActionPlanApi.reducerPath]: pillarsActionPlanApi.reducer,
  [registrantsInviteApi.reducerPath]: registrantsInviteApi.reducer,
  [reportingApi.reducerPath]: reportingApi.reducer,
  [organizationKitInventoryApi.reducerPath]:
    organizationKitInventoryApi.reducer,
  [eventsApi.reducerPath]: eventsApi.reducer,
  [ioApi.reducerPath]: ioApi.reducer,
  [notesApi.reducerPath]: notesApi.reducer,
  [inKindApi.reducerPath]: inKindApi.reducer,
  [eventParentsApi.reducerPath]: eventParentsApi.reducer,
  [duplicateManagementApi.reducerPath]: duplicateManagementApi.reducer,
});

const rootReducer: typeof appReducer = (state, action) => {
  if (action.type === 'USER_LOGOUT') {
    return appReducer(undefined, action);
  }

  return appReducer(state, action);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setupStore = (initialState: Record<string, any> = {}) =>
  configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware: any) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }).concat(
        userGrowApi.middleware,
        authApi.middleware,
        authNoHeaderApi.middleware,
        workshopsApi.middleware,
        workshopTasksApi.middleware,
        organizationsApi.middleware,
        registrantsApi.middleware,
        kitManagementApi.middleware,
        subscibersApi.middleware,
        workshopAppApi.middleware,
        lightboxApi.middleware,
        familyApi.middleware,
        tagsApi.middleware,
        peopleApi.middleware,
        elcsApi.middleware,
        storageApi.middleware,
        pillarsApi.middleware,
        pillarsActionPlanApi.middleware,
        registrantsInviteApi.middleware,
        reportingApi.middleware,
        organizationKitInventoryApi.middleware,
        eventsApi.middleware,
        ioApi.middleware,
        notesApi.middleware,
        inKindApi.middleware,
        eventParentsApi.middleware,
        duplicateManagementApi.middleware,
      ),
    preloadedState: initialState,
  });

export const store = setupStore();

export const clearAllCachedData = () => {
  store.dispatch(userGrowApi.util.resetApiState());
  store.dispatch(authApi.util.resetApiState());
  store.dispatch(authNoHeaderApi.util.resetApiState());
  store.dispatch(workshopsApi.util.resetApiState());
  store.dispatch(workshopTasksApi.util.resetApiState());
  store.dispatch(organizationsApi.util.resetApiState());
  store.dispatch(registrantsApi.util.resetApiState());
  store.dispatch(kitManagementApi.util.resetApiState());
  store.dispatch(subscibersApi.util.resetApiState());
  store.dispatch(workshopAppApi.util.resetApiState());
  store.dispatch(lightboxApi.util.resetApiState());
  store.dispatch(familyApi.util.resetApiState());
  store.dispatch(tagsApi.util.resetApiState());
  store.dispatch(peopleApi.util.resetApiState());
  store.dispatch(elcsApi.util.resetApiState());
  store.dispatch(storageApi.util.resetApiState());
  store.dispatch(pillarsApi.util.resetApiState());
  store.dispatch(pillarsActionPlanApi.util.resetApiState());
  store.dispatch(registrantsInviteApi.util.resetApiState());
  store.dispatch(reportingApi.util.resetApiState());
  store.dispatch(organizationKitInventoryApi.util.resetApiState());
  store.dispatch(eventsApi.util.resetApiState()); // Add this line
  store.dispatch(ioApi.util.resetApiState());
  store.dispatch(notesApi.util.resetApiState());
  store.dispatch(inKindApi.util.resetApiState());
  store.dispatch(eventParentsApi.util.resetApiState());
  store.dispatch(duplicateManagementApi.util.resetApiState());
};

export type RootState = ReturnType<typeof rootReducer>;
type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = cb =>
  useSelector(cb, shallowEqual);

setupListeners(store.dispatch);
